import {
  Divider,
  IconButton,
  Modal,
  Paper,
  Typography,
  Avatar,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import { Link, useParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';

import Donation from 'entities/donation/model';
import CapBalance from 'retired/CapBalance';
import NonprofitLink from 'retired/NonprofitLink';
import { currenciesFormat } from 'utils/currencyFormat';
import { lightGray, primaryBlue, yellow, red } from 'utils/color-styles';
import { DocumentViewer } from 'components/DocumentViewer/DocumentViewer';
import { Features } from 'utils/features';
import { selectUser } from 'entities/user/selectors';
import { externalDonationTypeLabels } from 'containers/FeaturesPage/constants';

import { MATCHING_REQUEST_STATUS_DISPLAY_KEYS, MATCHING_REQUEST_STATUSES } from './types';
import { formatDateWithoutTimezone } from './utils';
import { ConfidenceScoreChip } from './ConfidenceScoreChip';
import { AiFeedback } from './AiFeedback';

const styles = {
  approveBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: primaryBlue,
    borderRadius: 5,
    padding: '8px 20px',
    marginTop: 16,
  },
  divider: {
    margin: '16px 0',
  },
  matchableAmountContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  matchableAmountChip: {
    marginRight: 5,
    marginBottom: 5,
    height: 26,
    verticalAlign: 'top',
    backgroundColor: yellow,
    padding: 5,
    fontSize: 11,
    borderRadius: 5,
    whiteSpace: 'nowrap',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    padding: 16,
    width: 840,
    outline: 'none',
  },
  unmatchableBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: lightGray,
    borderRadius: 5,
    padding: '8px 20px',
    marginTop: 16,
    marginBottom: 16,
    height: 36,
  },
};

export const ExternalDonationRequestModal = ({
  modalOpen,
  onApprove,
  onDeny,
  selectedDonation,
  setModalOpen,
  statusUpdating,
}: {
  modalOpen: boolean;
  onApprove: () => void;
  onDeny: () => void;
  selectedDonation: Donation;
  setModalOpen: (value: boolean) => void;
  statusUpdating: Map<string, boolean>;
}) => {
  const {
    matchingRequestStatus,
  }: {
    matchingRequestStatus: MATCHING_REQUEST_STATUS_DISPLAY_KEYS;
  } = useParams();

  const user = useSelector(selectUser);

  let amount;
  let amountCurrencies;

  if (MATCHING_REQUEST_STATUSES[matchingRequestStatus] === MATCHING_REQUEST_STATUSES.pending) {
    amount = selectedDonation.matchableAmount;
    amountCurrencies = selectedDonation.matchableAmountCurrencies;
  } else {
    amount = selectedDonation.matchedAmount;
    amountCurrencies = selectedDonation.matchedAmountCurrencies;
  }

  const capBalance = selectedDonation.matchingBudgetId
    ? selectedDonation.user._capBalanceByBudget?.find(
        (b: any) => b.id === selectedDonation.matchingBudgetId
      )
    : undefined;
  const ifMatchableIsBalanceSufficient =
    (capBalance && !Number.isFinite(capBalance.balance)) ||
    (Number.isFinite(capBalance?.balance) &&
      capBalance.balance > 0 &&
      capBalance.balance >=
        (selectedDonation?.matchableRule?.matchingPercentage / 100) * selectedDonation.amount);
  const ifMatchableIsMatchableAmountCorrect =
    // @NOTE-RT: Use `<=` 'cause JS number comparisons are a bit iffy.
    (selectedDonation?.matchableRule?.matchingPercentage / 100) * selectedDonation.amount <= amount;
  const ifMatchableIsFullAmountMatchable =
    ifMatchableIsBalanceSufficient && ifMatchableIsMatchableAmountCorrect;
  // @NOTE-CH: If it's contribution based, then the matchable amount is calculated from the donation amount, and not from the matching amount
  const ifMatchableIsContributionBased = capBalance?.contributionBased;

  return (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      style={styles.modal}
      disableAutoFocus
    >
      <Paper style={styles.paper}>
        <Box display="flex">
          <Box width="50%" pr={1} display="flex" flexDirection="column">
            <Box flexGrow={1}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <Avatar
                    alt={selectedDonation.user.name}
                    src={selectedDonation.user.mainPicture?.replace('http://', 'https://')}
                    style={{
                      width: 48,
                      height: 48,
                      float: 'left',
                      marginRight: 18,
                    }}
                  />
                  <div style={{ display: 'inline-block' }}>
                    <Typography component="p" style={{ fontSize: 14 }}>
                      {selectedDonation.user.name}
                    </Typography>
                    {selectedDonation.user._capBalanceByBudget?.length > 0 && (
                      <Typography
                        component="p"
                        style={{
                          fontSize: 11,
                          color:
                            !ifMatchableIsFullAmountMatchable &&
                            MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                              MATCHING_REQUEST_STATUSES.pending
                              ? red
                              : 'unset',
                        }}
                      >
                        Match balance:{' '}
                        <CapBalance
                          capBalanceByBudget={selectedDonation.user._capBalanceByBudget}
                          unlimitedMessaging="unlimited in"
                        />
                      </Typography>
                    )}
                    <Typography component="p" style={{ fontSize: 11 }}>
                      <Link to={`/volunteers/${selectedDonation.user.id}`} className="hoverable">
                        View Profile
                      </Link>
                    </Typography>
                  </div>
                </div>
                <CloseIcon style={{ cursor: 'pointer' }} onClick={() => setModalOpen(false)} />
              </div>
              <Divider style={styles.divider} />
              {!selectedDonation.matchableAmount &&
                MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                  MATCHING_REQUEST_STATUSES.pending && (
                  <div style={styles.unmatchableBar}>
                    <Typography component="p" style={{ fontSize: 11 }}>
                      This donation is not eligible for matching
                    </Typography>
                  </div>
                )}
              <div style={{ display: 'flex', marginBottom: 16 }}>
                <Typography component="p" style={{ fontSize: 12, fontWeight: 'bold', width: 100 }}>
                  Nonprofit
                </Typography>
                <Typography component="p" style={{ fontSize: 12 }}>
                  {selectedDonation.nonprofits?.map((nonprofit, i) => (
                    <React.Fragment key={nonprofit?.id}>
                      <NonprofitLink nonprofit={nonprofit} />
                      {i + 1 < selectedDonation.nonprofits.size ? ', ' : ''}
                    </React.Fragment>
                  )) || '-'}
                </Typography>
              </div>
              <div style={{ display: 'flex', marginBottom: 16 }}>
                <Typography component="p" style={{ fontSize: 12, fontWeight: 'bold', width: 100 }}>
                  Amount
                </Typography>
                <Typography component="p" style={{ fontSize: 12 }}>
                  {currenciesFormat(selectedDonation.amountCurrencies, selectedDonation.currency)}
                </Typography>
              </div>
              <div style={{ display: 'flex', marginBottom: 16 }}>
                <Typography component="p" style={{ fontSize: 12, fontWeight: 'bold', width: 100 }}>
                  Donated
                </Typography>
                <Typography component="p" style={{ fontSize: 12 }}>
                  {formatDateWithoutTimezone(selectedDonation.date)}
                </Typography>
              </div>
              <div style={{ display: 'flex', marginBottom: 16 }}>
                <Typography component="p" style={{ fontSize: 12, fontWeight: 'bold', width: 100 }}>
                  Submitted
                </Typography>
                <Typography component="p" style={{ fontSize: 12 }}>
                  {formatDateWithoutTimezone(selectedDonation.createdAt)}
                </Typography>
              </div>
              <div style={{ display: 'flex', marginBottom: 16 }}>
                <Typography component="p" style={{ fontSize: 12, fontWeight: 'bold', width: 100 }}>
                  Receipt
                </Typography>
                <Typography component="p" style={{ fontSize: 12 }}>
                  <a
                    href={selectedDonation.receiptUrl}
                    className="hoverable"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View uploaded receipt
                  </a>
                </Typography>
              </div>
              {selectedDonation.externalDonationType && (
                <div style={{ display: 'flex', marginBottom: 16 }}>
                  <Typography
                    component="p"
                    style={{ fontSize: 12, fontWeight: 'bold', width: 100 }}
                  >
                    Donation Type
                  </Typography>
                  <Typography component="p" style={{ fontSize: 12 }}>
                    {externalDonationTypeLabels[selectedDonation.externalDonationType]}
                  </Typography>
                </div>
              )}
              {selectedDonation.designation && (
                <div style={{ display: 'flex', marginBottom: 16 }}>
                  <Typography
                    component="p"
                    style={{ fontSize: 12, fontWeight: 'bold', width: 100 }}
                  >
                    Designation
                  </Typography>
                  <Typography component="p" style={{ fontSize: 12 }}>
                    {selectedDonation.designation}
                  </Typography>
                </div>
              )}
              {selectedDonation.dedication && (
                <div style={{ display: 'flex', marginBottom: 16 }}>
                  <Typography
                    component="p"
                    style={{ fontSize: 12, fontWeight: 'bold', width: 100 }}
                  >
                    Dedication
                  </Typography>
                  <Typography component="p" style={{ fontSize: 12 }}>
                    {selectedDonation.dedication}
                  </Typography>
                </div>
              )}
              {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                MATCHING_REQUEST_STATUSES.pending && (
                <div style={{ display: 'flex', marginBottom: 16 }}>
                  <Typography
                    component="p"
                    style={{ fontSize: 12, fontWeight: 'bold', width: 100 }}
                  >
                    Match Rule
                  </Typography>
                  <Typography component="p" style={{ fontSize: 12 }}>
                    {selectedDonation.matchableRule?.matchingPercentage
                      ? `${selectedDonation.matchableRule.title} · ${selectedDonation.matchableRule.matchingPercentage}%`
                      : 'Not Eligible'}
                  </Typography>
                </div>
              )}
              {MATCHING_REQUEST_STATUSES[matchingRequestStatus] !==
                MATCHING_REQUEST_STATUSES.denied && (
                <div style={{ display: 'flex', marginBottom: 16 }}>
                  <Typography
                    component="p"
                    style={{
                      fontSize: 12,
                      fontWeight: 'bold',
                      width: 100,
                    }}
                  >
                    {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                      MATCHING_REQUEST_STATUSES.pending && 'Best Match'}
                    {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                      MATCHING_REQUEST_STATUSES.approved && 'Matched'}
                  </Typography>
                  <Typography component="p" style={{ fontSize: 12 }}>
                    {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                      MATCHING_REQUEST_STATUSES.pending && amount > 0 ? (
                      <div style={styles.matchableAmountContainer}>
                        <span style={styles.matchableAmountChip}>
                          {currenciesFormat(amountCurrencies, selectedDonation.currency)}
                        </span>
                        {!ifMatchableIsFullAmountMatchable && (
                          <Typography
                            component="span"
                            style={{
                              fontSize: 9,
                              color: ifMatchableIsContributionBased ? yellow : red,
                              marginLeft: 4,
                              marginTop: 4,
                            }}
                          >
                            The full amount is not matchable due to matching cap limits
                          </Typography>
                        )}
                      </div>
                    ) : (
                      currenciesFormat(amountCurrencies, selectedDonation.currency)
                    )}
                  </Typography>
                </div>
              )}

              {user?.hasFeature(Features.AI_RECEIPT_READER) &&
                typeof selectedDonation.donationReceipt?.confidenceScore === 'number' && (
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Typography
                      component="p"
                      style={{ fontSize: 12, fontWeight: 'bold', width: 100, flexShrink: 0 }}
                    >
                      AI Review
                    </Typography>
                    <Typography component="p" style={{ fontSize: 12 }}>
                      <ConfidenceScoreChip
                        matchable={selectedDonation.matchableAmount > 0}
                        confidenceScore={selectedDonation.donationReceipt.confidenceScore}
                      />
                      {!(selectedDonation.matchableAmount > 0) && (
                        <Typography component="p" style={{ fontSize: 12, marginTop: 12 }}>
                          This match request is currently not eligible under your company’s matching
                          rules. It cannot be matched at this time because there is no matching
                          budget or program availability for this type of donation.
                        </Typography>
                      )}
                      <AiFeedback donation={selectedDonation} />
                    </Typography>
                  </div>
                )}
            </Box>

            <div style={styles.approveBar}>
              <Typography
                component="p"
                style={{ fontSize: 12, lineHeight: '22px', color: 'white' }}
              >
                {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                  MATCHING_REQUEST_STATUSES.approved &&
                  `Approved on: ${formatDateWithoutTimezone(selectedDonation.matchedAt)}`}
                {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                  MATCHING_REQUEST_STATUSES.denied &&
                  `Denied on: ${formatDateWithoutTimezone(
                    selectedDonation.matchRequestRejectedAt
                  )}`}
                {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
                  MATCHING_REQUEST_STATUSES.pending && 'Approve / Deny'}
              </Typography>
              {MATCHING_REQUEST_STATUSES[matchingRequestStatus] ===
              MATCHING_REQUEST_STATUSES.pending ? (
                <div>
                  {statusUpdating.get(selectedDonation.id) ? (
                    <CircularProgress
                      size={24}
                      style={{ margin: '9px 12px 8px', color: 'white' }}
                    />
                  ) : (
                    <>
                      {!selectedDonation.matchableAmount ? (
                        <Tooltip title="Cannot approve unmatchable requests">
                          <span>
                            <IconButton disabled>
                              <CheckIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <IconButton title="Approve" onClick={onApprove} style={{ color: 'white' }}>
                          <CheckIcon />
                        </IconButton>
                      )}
                      <IconButton title="Deny" onClick={onDeny}>
                        <BlockIcon style={{ color: 'white' }} />
                      </IconButton>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ height: '48px' }} />
              )}
            </div>
          </Box>

          <Box width="50%" pl={1}>
            <DocumentViewer receiptUrl={selectedDonation.receiptUrl} width={353} />
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default ExternalDonationRequestModal;
